import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getResponseData } from '../models/api-response.model';
import { Product } from '../models/product.model';
import { ApiService } from './api.service';
import { I18nService } from './i18n.service';

/** Mirrors backend WishlistItemDto (subset used by storefront). */
export interface WishlistItemDto {
  id: number;
  productId: number;
  branchId: number;
  product?: Product;
}

@Injectable({
  providedIn: 'root',
})
export class WishlistService {
  private readonly branchId = environment.branchId;
  private readonly itemsSubject = new BehaviorSubject<WishlistItemDto[]>([]);

  readonly wishlist$ = this.itemsSubject.asObservable();
  readonly wishlistIds$ = this.wishlist$.pipe(
    map((items) => new Set(items.map((i) => i.productId)))
  );

  constructor(
    private api: ApiService,
    private i18n: I18nService
  ) {}

  /** Synchronous membership check for cards / PDP. */
  isInWishlist(productId: number): boolean {
    const pid = Number(productId);
    return this.itemsSubject.getValue().some((i) => i.productId === pid);
  }

  /** Snapshot of current rows. */
  get itemsSnapshot(): WishlistItemDto[] {
    return this.itemsSubject.getValue();
  }

  /** Full wishlist with product payloads; seeds the shared subject. */
  getWishlist(): Observable<WishlistItemDto[]> {
    return this.refresh();
  }

  /** Reloads wishlist from the API into the shared subject. */
  refresh(): Observable<WishlistItemDto[]> {
    const bid = Number(this.branchId) || 1;
    return this.api.get<WishlistItemDto[]>(`ProductFavourite/me/${bid}`).pipe(
      map((response) => getResponseData(response, [] as WishlistItemDto[])),
      tap((rows) => this.itemsSubject.next(rows ?? []))
    );
  }

  /**
   * Optimistically adds a product, then reconciles with the POST response.
   * Callers may toast / flip hearts immediately.
   */
  add(productId: number, productHint?: Product | null): Observable<WishlistItemDto> {
    const pid = Number(productId);
    if (!pid || pid <= 0) {
      return throwError(() => new Error(this.i18n.t('product.invalid_product')));
    }

    const previous = this.itemsSubject.getValue();
    if (previous.some((i) => i.productId === pid)) {
      return of(previous.find((i) => i.productId === pid)!);
    }

    const branchId = Number(this.branchId) || 1;
    const optimistic: WishlistItemDto = {
      id: -Date.now(),
      productId: pid,
      branchId,
      product: productHint ?? undefined,
    };
    this.itemsSubject.next([...previous, optimistic]);

    return this.api
      .post<WishlistItemDto>('ProductFavourite/AddProductFavourite', {
        productId: pid,
        branchId,
      })
      .pipe(
        map((response) => getResponseData(response, {} as WishlistItemDto)),
        tap((serverItem) => {
          const current = this.itemsSubject.getValue();
          const withoutTemp = current.filter((i) => i.productId !== pid);
          const nextItem: WishlistItemDto = {
            id: serverItem?.id ?? optimistic.id,
            productId: pid,
            branchId: serverItem?.branchId ?? branchId,
            product: serverItem?.product ?? productHint ?? undefined,
          };
          this.itemsSubject.next([...withoutTemp, nextItem]);
        }),
        catchError((err: unknown) => {
          this.itemsSubject.next(previous);
          return throwError(() => err);
        })
      );
  }

  /**
   * Optimistically removes a product, then DELETEs on the server.
   * Callers may toast / flip hearts immediately; errors restore the snapshot.
   */
  remove(productId: number): Observable<boolean> {
    const pid = Number(productId);
    const previous = this.itemsSubject.getValue();
    const existing = previous.find((i) => i.productId === pid);
    if (!existing) {
      return of(true);
    }

    this.itemsSubject.next(previous.filter((i) => i.productId !== pid));

    if (existing.id < 0) {
      return of(true);
    }

    const branchId = Number(this.branchId) || 1;
    return this.api
      .delete<boolean>('ProductFavourite/DeleteProductFromFavourite', {
        productId: pid,
        branchId,
      })
      .pipe(
        map((response) => getResponseData(response, false)),
        catchError((err: unknown) => {
          this.itemsSubject.next(previous);
          return throwError(() => err);
        })
      );
  }
}
